<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 700));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "radial-gradient(circle at 30% 20%, #1a0040 0%, #060010 70%)",
      fontFamily:
        props.block.fontFamily || "'Orbitron', 'Rajdhani', sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.2}px`,
    height: `${base * 2.4}px`,
    fontSize: `${base}px`,
    radius: `${props.block.cardRadius ?? 14}px`,
  };
});
</script>

<template>
  <div class="cd-neon" :style="containerStyle">
    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-neon__card"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 200}ms`,
      }"
    >
      <span class="cd-neon__border" />
      <span class="cd-neon__inner">
        <span
          class="cd-neon__digit"
          :key="seg.value"
          :style="{
            fontSize: cardSize.fontSize,
            color: block.digitColor || '#ffffff',
          }"
        >
          {{ seg.value }}
        </span>
        <span
          class="cd-neon__label"
          :style="{
            fontSize: `${block.labelFontSize}px`,
            color: block.labelColor || 'rgba(255,255,255,0.7)',
          }"
        >
          {{ seg.label }}
        </span>
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-neon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  overflow: hidden;
  isolation: isolate;
}
.cd-neon::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 70% 80%, rgba(255, 0, 200, 0.3), transparent 60%),
    radial-gradient(circle at 20% 30%, rgba(0, 255, 255, 0.25), transparent 55%);
  pointer-events: none;
  z-index: 0;
}
.cd-neon__card {
  position: relative;
  z-index: 1;
  padding: 3px;
  background: transparent;
  animation: cd-neon-rise 700ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
@keyframes cd-neon-rise {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.cd-neon__border {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: conic-gradient(from 0deg, #00ffff, #ff00e6, #7c3aed, #00ffff);
  animation: cd-neon-spin 4s linear infinite;
  filter: blur(0.5px);
}
@keyframes cd-neon-spin {
  to {
    transform: rotate(360deg);
  }
}
.cd-neon__inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: calc(var(--cd-radius, 14px) - 2px);
  background: rgba(8, 0, 24, 0.92);
  box-shadow:
    inset 0 0 20px rgba(124, 58, 237, 0.4),
    0 0 30px rgba(0, 255, 255, 0.25);
  border-radius: inherit;
}
.cd-neon__inner::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: inset 0 0 30px rgba(255, 0, 230, 0.18);
  pointer-events: none;
  animation: cd-neon-pulse 2.4s ease-in-out infinite;
}
@keyframes cd-neon-pulse {
  0%,
  100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}
.cd-neon__digit {
  font-weight: 800;
  letter-spacing: 0.05em;
  font-variant-numeric: tabular-nums;
  text-shadow:
    0 0 10px rgba(0, 255, 255, 0.9),
    0 0 22px rgba(255, 0, 230, 0.6);
  display: inline-block;
  animation: cd-neon-flash var(--cd-tick, 700ms) ease-out;
}
@keyframes cd-neon-flash {
  0% {
    transform: scale(1.15);
    filter: brightness(1.6);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
}
.cd-neon__label {
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-weight: 500;
}
</style>
